/**
 * WaitlistForm — what /register offers while sign-ups are closed
 * (config `auth/signups` ≠ open). The address, an explicit consent with its
 * privacy notice (GDPR Art. 6/7), and a honeypot field people never see.
 * The answer is the same whether the address was new or already listed.
 *
 * `prefill` comes from the static landing form (GET /register?email&consent):
 * when the landing already collected the consent checkbox, the request is sent
 * at once (double opt-in still guards the address).
 */
import React, { useEffect, useRef, useState } from 'react'

import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { joinWaitlist } from '@/platform/auth/services/waitlist.service'
import { useTheme } from '@/shared/hooks/useTheme'
import { ApiException } from '@/shared/utils/api'
import { LEGAL_URLS } from '@/shared/utils/constants'

export interface WaitlistPrefill {
  email?: string
  consent?: boolean
  source?: string
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
}

interface WaitlistFormProps {
  /** Where the person joined from (landing, register, login…). */
  source: string
  prefill?: WaitlistPrefill
  /** Called with the address the request was sent for. */
  onJoined?: (email: string) => void
}

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

export const WaitlistForm: React.FC<WaitlistFormProps> = ({ source, prefill, onJoined }) => {
  const { t, i18n } = useTranslation('auth')
  const { isDark } = useTheme()
  const [email, setEmail] = useState(prefill?.email ?? '')
  const [consent, setConsent] = useState(prefill?.consent ?? false)
  const [website, setWebsite] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const autoSent = useRef(false)

  const submit = async (address: string, agreed: boolean): Promise<void> => {
    setError(null)
    if (!EMAIL_PATTERN.test(address.trim())) {
      setError(t('waitlist.invalidEmail'))
      return
    }
    if (!agreed) {
      setError(t('waitlist.consentRequired'))
      return
    }
    setIsLoading(true)
    try {
      await joinWaitlist({
        email: address.trim(),
        consent: true,
        locale: (i18n.language || 'en').slice(0, 2),
        source: prefill?.source ?? source,
        ...(prefill?.utmSource ? { utmSource: prefill.utmSource } : {}),
        ...(prefill?.utmMedium ? { utmMedium: prefill.utmMedium } : {}),
        ...(prefill?.utmCampaign ? { utmCampaign: prefill.utmCampaign } : {}),
        ...(document.referrer ? { referrer: document.referrer } : {}),
        ...(website ? { website } : {}),
      })
      onJoined?.(address.trim())
    } catch (e) {
      const code = e instanceof ApiException ? e.code : undefined
      setError(
        code === 'CONSENT_REQUIRED'
          ? t('waitlist.consentRequired')
          : code === 'RATE_LIMITED'
            ? t('waitlist.rateLimited')
            : code === 'VALIDATION_ERROR'
              ? t('waitlist.invalidEmail')
              : t('waitlist.genericError'),
      )
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!autoSent.current && prefill?.email && prefill.consent) {
      autoSent.current = true
      void submit(prefill.email, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, for the landing hand-off
  }, [])

  const mutedClasses = isDark ? 'text-crypto-muted' : 'text-gray-600'
  const inputClasses = isDark
    ? 'bg-crypto-dark border-crypto-border text-crypto-text placeholder-crypto-muted'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'

  return (
    <form
      className="space-y-4"
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        void submit(email, consent)
      }}
      data-testid="waitlist-form"
    >
      <div>
        <label htmlFor="waitlist-email" className={`block text-sm font-medium mb-1 ${mutedClasses}`}>
          {t('waitlist.emailLabel')}
        </label>
        <input
          id="waitlist-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('waitlist.emailPlaceholder')}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-crypto-accent ${inputClasses}`}
          disabled={isLoading}
          aria-invalid={error === t('waitlist.invalidEmail')}
          aria-describedby={error ? 'waitlist-error' : undefined}
          data-testid="waitlist-email-input"
        />
      </div>

      {/* Honeypot: off-screen, unreachable by keyboard, ignored by password managers */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
        <label htmlFor="waitlist-website">Website</label>
        <input
          id="waitlist-website"
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
        />
      </div>

      <label className="flex items-start space-x-3">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className={`mt-1 h-4 w-4 text-crypto-accent rounded focus:ring-crypto-accent focus:ring-2 ${
            isDark ? 'border-crypto-border' : 'border-gray-300'
          }`}
          disabled={isLoading}
          data-testid="waitlist-consent-checkbox"
        />
        <span className={`text-sm ${mutedClasses}`}>
          {t('waitlist.consent')}{' '}
          <a
            href={LEGAL_URLS.PRIVACY}
            target="_blank"
            rel="noopener noreferrer"
            className="text-crypto-accent hover:text-crypto-accent-light underline"
            data-testid="waitlist-privacy-link"
          >
            {t('waitlist.privacyLink')}
          </a>
          .
        </span>
      </label>

      <details id="waitlist-privacy" className={`text-xs ${mutedClasses}`} data-testid="waitlist-privacy">
        <summary className="cursor-pointer">{t('waitlist.privacyTitle')}</summary>
        <p className="mt-2 leading-relaxed">{t('waitlist.privacyBody')}</p>
      </details>

      {error && (
        <div
          id="waitlist-error"
          role="alert"
          className="p-3 text-sm rounded-lg border border-red-500/30 bg-red-500/10 text-red-400 [html[data-theme=light]_&]:text-red-700"
          data-testid="waitlist-error"
        >
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={isLoading}
        className="w-full py-3 px-4 bg-crypto-accent hover:bg-crypto-accent-light text-white rounded-lg font-medium transition-colors disabled:opacity-50"
        data-testid="waitlist-submit"
      >
        {isLoading ? t('waitlist.submitting') : t('waitlist.submit')}
      </button>
    </form>
  )
}
